/**
 * ImageEditorPage
 *
 * Vertex AI Image Editor - Main Page
 * 3-Column Layout: Upload | Canvas | Settings
 * Route: /v2/tools/image-editor
 * @accessibility WCAG 2.1 AA konform
 */

import { useState, useCallback } from 'react';
import { useImageEditor } from '@/hooks/useImageEditor';
import { useImageGeneration } from '@/hooks/useImageGeneration';
import { AssetContextPanelV2 } from '@/components/v2/tools/generation/AssetContextPanelV2';
import { ImageEditorCanvas } from '@/components/image-editor/ImageEditorCanvas';
import { ImageEditPanel } from '@/components/image-editor/ImageEditPanel';
import { ImageEditResults } from '@/components/image-editor/ImageEditResults';
import { MiniGalleryPreview } from '@/components/v2/tools/generation/MiniGalleryPreview';

// Panel size configuration (same as StudioSingleShot)
const LEFT_PANEL_WIDTH = 280;
const RIGHT_PANEL_WIDTH = 320;

export function ImageEditorPage() {
  const {
    selectedTask,
    setSelectedTask,
    originalImage,
    setOriginalImage,
    preview,
    mask,
    setMask,
    generatedImages,
    setGeneratedImages,
    isGenerating,
    setIsGenerating,
    targetAspectRatio,
    setTargetAspectRatio,
  } = useImageEditor();

  // Debug: Log current state
  console.log('🔍 ImageEditorPage state:', {
    originalImage: originalImage?.name,
    preview,
    selectedTask
  });

  const {
    generateInpaintAdd,
    generateInpaintRemove,
    generateOutpaint,
    generateBackgroundEdit,
  } = useImageGeneration();

  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null);
  const [leftPanelTab, setLeftPanelTab] = useState<'upload' | 'gallery' | 'product'>('upload');

  const handleFileChange = useCallback((file: File | null) => {
    console.log('📁 File changed:', file);
    setOriginalImage(file);
  }, [setOriginalImage]);

  const handleGenerate = useCallback(async (params: {
    prompt: string;
    numberOfImages: number;
    negativePrompt?: string;
  }) => {
    if (!originalImage || !mask) {
      alert('Bitte wähle ein Bild und zeichne eine Maske');
      return;
    }

    setIsGenerating(true);
    try {
      let images;
      const genParams = {
        image: originalImage,
        mask,
        prompt: params.prompt,
        numberOfImages: params.numberOfImages,
        negativePrompt: params.negativePrompt,
      };

      switch (selectedTask) {
        case 'inpaint_add':
          images = await generateInpaintAdd(genParams);
          break;
        case 'inpaint_remove':
          images = await generateInpaintRemove(genParams);
          break;
        case 'outpaint':
          images = await generateOutpaint({
            ...genParams,
            ratio: targetAspectRatio, // Send selected aspect ratio
          });
          break;
        case 'background_edit':
          images = await generateBackgroundEdit(genParams);
          break;
      }

      setGeneratedImages(images);
    } catch (error) {
      console.error('Generation failed:', error);
    } finally {
      setIsGenerating(false);
    }
  }, [
    originalImage,
    mask,
    selectedTask,
    targetAspectRatio,
    generateInpaintAdd,
    generateInpaintRemove,
    generateOutpaint,
    generateBackgroundEdit,
    setGeneratedImages,
    setIsGenerating,
  ]);

  const handleDownload = useCallback((image: { id: string; image_url: string }) => {
    const link = document.createElement('a');
    link.href = image.image_url;
    link.download = `edited-${image.id}.png`;
    link.click();
  }, []);

  const handleSelect = useCallback((image: { id: string; image_url: string }) => {
    console.log('Selected image:', image);
  }, []);

  return (
    <>
      <div id="image-editor-workspace" className="flex flex-col h-[98%]">
        {/* 3-Column Layout - Full Height */}
        <div id="editor-workspace" className="editor-workspace flex flex-1 overflow-hidden">
          {/* LEFT: Asset & Context Panel */}
          <aside
            id="asset-context-panel"
            className="asset-context-panel flex-shrink-0 border-r border-neutral-200 dark:border-neutral-800"
            style={{ width: `${LEFT_PANEL_WIDTH}px` }}
          >
            <AssetContextPanelV2
              selectedFile={originalImage}
              preview={preview}
              onFileChange={handleFileChange}
              selectedBrandId={selectedBrandId}
              onBrandChange={setSelectedBrandId}
              activeTab={leftPanelTab}
              onTabChange={setLeftPanelTab}
              mask={mask}
            />
          </aside>

          {/* MIDDLE: Canvas Panel */}
          <section
            id="canvas-panel"
            className="canvas-panel flex-1 border-r border-neutral-200 dark:border-neutral-800 flex flex-col"
          >
            <ImageEditorCanvas
              imageUrl={preview}
              onMaskChange={setMask}
              selectedTask={selectedTask}
              targetAspectRatio={targetAspectRatio}
              setTargetAspectRatio={setTargetAspectRatio}
            />
            {generatedImages.length > 0 && (
              <ImageEditResults
                images={generatedImages}
                onDownload={handleDownload}
                onSelect={handleSelect}
              />
            )}
          </section>

          {/* RIGHT: Edit Settings Panel */}
          <aside
            id="edit-settings-panel"
            className="edit-settings-panel flex-shrink-0 border-l border-neutral-200 dark:border-neutral-800"
            style={{ width: `${RIGHT_PANEL_WIDTH}px` }}
          >
            <ImageEditPanel
              selectedTask={selectedTask}
              onTaskChange={setSelectedTask}
              onGenerate={handleGenerate}
              isGenerating={isGenerating}
              disabled={!originalImage || !mask}
            />
          </aside>
        </div>
      </div>

      {/* Mini Gallery Preview (bottom of screen) */}
      <MiniGalleryPreview
        onImageClick={(imageId) => console.log('Gallery image clicked:', imageId)}
      />
    </>
  );
}
